import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applyActionCode, verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import { Loader, CheckCircle, AlertCircle, Lock, Eye, EyeOff } from 'lucide-react';
import { Btn, Input } from '../components/ui';
import { validatePassword } from '../utils';

const AuthActionPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetOobCode, setResetOobCode] = useState('');
  const { t } = useTranslation();

  useEffect(() => {
    const mode = searchParams.get('mode');
    const oobCode = searchParams.get('oobCode');

    if (!oobCode) {
      setStatus('error');
      setErrorMsg(t('authAction.error.invalidLink'));
      return;
    }

    if (mode === 'verifyEmail') {
      applyActionCode(auth, oobCode)
        .then(async () => {
          if (auth.currentUser) {
            await auth.currentUser.reload();
            await auth.currentUser.getIdToken(true);
            window.location.href = '/';
            return;
          }
          setStatus('success');
        })
        .catch(async () => {
          if (auth.currentUser) {
            await auth.currentUser.reload();
            if (auth.currentUser.emailVerified) {
              await auth.currentUser.getIdToken(true);
              window.location.href = '/';
              return;
            }
          }
          setStatus('error');
          setErrorMsg(t('authAction.error.usedLink'));
        });
    } else if (mode === 'resetPassword') {
      verifyPasswordResetCode(auth, oobCode)
        .then((email) => {
          setResetEmail(email);
          setResetOobCode(oobCode);
          setStatus('resetForm');
        })
        .catch(() => {
          setStatus('error');
          setErrorMsg(t('authAction.error.invalidReset'));
        });
    } else {
      setStatus('error');
      setErrorMsg(t('authAction.error.unknownAction'));
    }
  }, [searchParams, t]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const pw = validatePassword(newPassword);
    if (pw.errors.length > 0) {
      setErrorMsg(pw.errors[0]);
      return;
    }
    setResetLoading(true);
    setErrorMsg('');
    try {
      await confirmPasswordReset(auth, resetOobCode, newPassword);
      setStatus('resetSuccess');
    } catch (_err) {
      setErrorMsg(t('authAction.error.resetError'));
    }
    setResetLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 px-4">
      <Helmet>
        <title>{t('authAction.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/auth/action" />
      </Helmet>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl p-8 max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader className="w-12 h-12 text-rose-500 animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t('authAction.loading.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              {t('authAction.loading.desc')}
            </p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mx-auto flex items-center justify-center mb-4">
              <CheckCircle size={32} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t('authAction.success.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              {t('authAction.success.desc')}
            </p>
            <Btn onClick={() => (window.location.href = '/')} className="mx-auto">
              {t('authAction.success.cta')}
            </Btn>
          </>
        )}
        {status === 'resetForm' && (
          <>
            <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-fuchsia-600 rounded-full mx-auto flex items-center justify-center text-white mb-4">
              <Lock size={28} />
            </div>
            <h2 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t('authAction.resetForm.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              {t('authAction.resetForm.desc')} <strong>{resetEmail}</strong>
            </p>
            {errorMsg && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-xl text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {errorMsg}
              </div>
            )}
            <form onSubmit={handleResetPassword} className="space-y-4 text-left">
              <Input
                type={showPassword ? 'text' : 'password'}
                label={t('authAction.resetForm.label')}
                icon={Lock}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                placeholder={t('authAction.resetForm.placeholder')}
                suffix={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={
                      showPassword
                        ? t('authAction.resetForm.hidePassword')
                        : t('authAction.resetForm.showPassword')
                    }
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                }
              />
              {newPassword.length > 0 && (
                <div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4].map((level) => (
                      <div
                        key={level}
                        className={`h-1.5 flex-1 rounded-full ${
                          validatePassword(newPassword).strength >= level
                            ? [
                                '',
                                'bg-red-400',
                                'bg-orange-400',
                                'bg-yellow-400',
                                'bg-emerald-400',
                              ][level]
                            : 'bg-gray-200 dark:bg-gray-700'
                        }`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t(validatePassword(newPassword).labelKey)}
                  </p>
                </div>
              )}
              <Btn type="submit" className="w-full" loading={resetLoading}>
                {t('authAction.resetForm.submit')}
              </Btn>
            </form>
          </>
        )}
        {status === 'resetSuccess' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full mx-auto flex items-center justify-center mb-4">
              <CheckCircle size={32} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t('authAction.resetSuccess.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">
              {t('authAction.resetSuccess.desc')}
            </p>
            <Btn onClick={() => (window.location.href = '/')} className="mx-auto">
              {t('authAction.resetSuccess.cta')}
            </Btn>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full mx-auto flex items-center justify-center mb-4">
              <AlertCircle size={32} className="text-red-600 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold mb-2 dark:text-gray-100">
              {t('authAction.error.title')}
            </h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mb-6">{errorMsg}</p>
            <Btn onClick={() => (window.location.href = '/')} className="mx-auto">
              {t('authAction.error.cta')}
            </Btn>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthActionPage;
